import {useEffect, useState} from 'react';
import styles from './transferDetails.module.scss';
import {useMutation} from '@apollo/client';
import {PRE_SIGN_MEDIA_URL} from 'graphql/mutations/wallet';
import {toast} from 'react-toastify';
import {Spin} from 'antd';
import {CheckOutlined, CopyOutlined} from '@ant-design/icons';
import {fullyDecodeURL} from 'utils/helper';

const isInlineImage = url => typeof url === 'string' && url.startsWith('data:');

const parseS3Url = url => {
  if (!url || typeof url !== 'string' || !url.includes('amazonaws.com')) {
    return null;
  }

  try {
    const parsedUrl = new URL(url);
    const bucket = parsedUrl.hostname.split('.')[0];
    const filename = decodeURIComponent(parsedUrl.pathname).replace(/^\//, '');

    return bucket && filename ? {bucket, filename} : null;
  } catch {
    return null;
  }
};

const WireTransferDetails = ({data}) => {
  const details = data?.paymentMetadata || data || {};
  const bankInfo = details?.bankInformation;
  const mobileMoneyInfo = details?.mobileMoneyInformation;
  const paymentProvider =
    details?.paymentProvider ||
    details?.provider ||
    bankInfo?.paymentProvider ||
    details?.paymentMethod;
  const paymentMethod = details?.paymentMethod || bankInfo?.paymentMethod;
  const isInteracProvider = String(paymentProvider || '')
    .toUpperCase()
    .includes('INTERAC');
  const interacEmail = bankInfo?.email || details?.email;
  const recipientAddress =
    bankInfo?.recipientAddress ||
    bankInfo?.beneficiaryAddress ||
    bankInfo?.address ||
    details?.recipientAddress;
  const [presignedUrls, setPresignedUrls] = useState([]);
  const [viewReceipt, setViewReceipt] = useState(false);
  const [copiedField, setCopiedField] = useState('');
  const [presignMediaURL, {loading}] = useMutation(PRE_SIGN_MEDIA_URL, {
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });

  useEffect(() => {
    if (details?.bankInformation) {
      setPresignedUrls([]);
      setViewReceipt(false);
    }
  }, [details]);

  // Resolves one stored image to something an <img> can load: inline base64
  // as-is, an S3 object via a presigned URL. Null when it cannot be shown.
  const presignOne = async imageUrl => {
    // Receipts uploaded by older web builds were stored inline as base64 data
    // URLs rather than S3 objects. There is nothing to presign — the image is
    // the value — and refusing them with "Invalid AWS URL format" was why
    // those transactions looked like they had no proof at all.
    if (isInlineImage(imageUrl)) return imageUrl;

    const parsedImageUrl = parseS3Url(imageUrl);
    if (!parsedImageUrl) return null;

    const {bucket, filename} = parsedImageUrl;
    try {
      const {data} = await presignMediaURL({
        variables: {
          input: {
            filename: fullyDecodeURL(filename),
            bucket,
            upload: false,
          },
        },
      });
      const result = data?.presignMediaURL;
      if (result?.__typename === 'Error') return null;
      return result?.data?.url || null;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const generatePreSignedURLs = async () => {
    const urls = await Promise.all(images.map(presignOne));
    const failed = urls.filter(url => !url).length;
    if (failed === urls.length) {
      toast.error(
        urls.length > 1
          ? 'Could not load any of the images'
          : 'Could not load the image',
      );
      return [];
    }
    if (failed) {
      toast.error(`${failed} of ${urls.length} images could not be loaded`);
    }
    setPresignedUrls(urls);
    return urls;
  };

  const handleToggleReceipt = async () => {
    if (viewReceipt) {
      setViewReceipt(false);
      return;
    }

    setViewReceipt(true);
    const generated = await generatePreSignedURLs();
    if (!generated.length) setViewReceipt(false);
  };

  const downloadUrl = (url, name) => {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.download = name;
    // A data: URL downloads straight from the attribute; no new tab needed.
    if (isInlineImage(url)) link.removeAttribute('target');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // With several receipts the browser would block all but the first tab, so
  // the button opens the receipts and each one gets its own download link.
  const handleDownloadImage = async () => {
    if (images.length > 1) {
      if (!viewReceipt) await handleToggleReceipt();
      return;
    }
    const urls = presignedUrls.length
      ? presignedUrls
      : await generatePreSignedURLs();
    if (urls[0]) {
      downloadUrl(
        urls[0],
        receiptImages.length
          ? 'wire-transfer-receipt'
          : 'bank-withdrawal-instructions',
      );
    }
  };

  const hasValue = value =>
    value !== null && value !== undefined && String(value).trim() !== '';

  // Every receipt the user attached. The server lists them in
  // wireTransferReceiptImages (legacy single receipt included); the fallback
  // covers data from before that list was sent.
  const receiptImages = (
    Array.isArray(details?.wireTransferReceiptImages) &&
    details.wireTransferReceiptImages.length
      ? details.wireTransferReceiptImages
      : [bankInfo?.wireTransferReceiptImage]
  ).filter(hasValue);
  const images = receiptImages.length
    ? receiptImages
    : [bankInfo?.bankTransferInstructionImageURL].filter(hasValue);

  const handleCopyValue = async (value, key) => {
    if (!hasValue(value)) return;

    try {
      await navigator.clipboard.writeText(String(value));
      setCopiedField(key);
      setTimeout(() => setCopiedField(''), 1200);
    } catch (error) {
      toast.error('Unable to copy value');
    }
  };

  const renderDetailGrid = fields => {
    const availableFields = fields.filter(field => hasValue(field.value));

    if (!availableFields.length) {
      return (
        <div className={styles.emptyState}>
          No payment details available for this transaction.
        </div>
      );
    }

    return (
      <div className={styles.contents}>
        {availableFields.map(field => {
          const fieldKey = field.label;

          return (
            <div className={styles.content} key={field.label}>
              <div className={styles.contentHeader}>
                <h3>{field.label}</h3>
                <button
                  type="button"
                  className={styles.copyButton}
                  onClick={() => handleCopyValue(field.value, fieldKey)}
                  aria-label={`Copy ${field.label}`}>
                  {copiedField === fieldKey ? (
                    <CheckOutlined />
                  ) : (
                    <CopyOutlined />
                  )}
                </button>
              </div>
              <p className={styles.valueText}>{field.value}</p>
            </div>
          );
        })}
      </div>
    );
  };

  // The two are separate fields now, so both get their own row and ops copies
  // each into its own box on the wire form. A record written before the split
  // has only accountNumber, with accountNumberType saying which it really is —
  // so that one is still relabelled, and its IBAN row stays hidden.
  const isLegacyIbanAccount =
    !bankInfo?.iban &&
    String(bankInfo?.accountNumberType || '').toUpperCase() === 'IBAN';

  // The bank's beneficiary field takes a single line, so the parts are joined
  // in postal order and whichever ones the user never supplied simply drop
  // out; with none of them present the row is empty and gets filtered away.
  const holderAddress = bankInfo?.holderAddress;
  const holderAddressText = [
    holderAddress?.street,
    holderAddress?.city,
    holderAddress?.state,
    holderAddress?.postalCode,
    holderAddress?.country,
  ]
    .filter(part => hasValue(part))
    .map(part => String(part).trim())
    .join(', ');

  const bankDetailFields = [
    {label: 'Account Name', value: bankInfo?.accountName},
    {
      label: isLegacyIbanAccount ? 'IBAN' : 'Account Number',
      value: bankInfo?.accountNumber,
    },
    {label: 'IBAN', value: bankInfo?.iban},
    // Checking vs savings: some wire forms ask for it, and it is on the saved
    // account, so it travels in the snapshot rather than being chased.
    {label: 'Account Type', value: bankInfo?.accountType},
    {label: 'Bank Name', value: bankInfo?.bankName},
    {label: 'Bank Country', value: bankInfo?.bankCountry},
    {label: 'Routing Number', value: bankInfo?.routingNumber},
    {label: 'Swift Code', value: bankInfo?.swiftCode},
    {label: 'Tag Name', value: bankInfo?.tagName},
    // The user's own nickname for the account — useful when someone has
    // several at the same bank and support is asking which one they meant.
    {label: 'Account Alias', value: bankInfo?.accountAlias},
    {label: 'Account Holder Address', value: holderAddressText},
    {label: 'Recipient Address', value: recipientAddress},
    ...(isInteracProvider
      ? [{label: 'Interac Email', value: interacEmail}]
      : []),
  ];

  const mobileMoneyFields = [
    {label: 'Beneficiary Name', value: mobileMoneyInfo?.beneficiaryName},
    {label: 'Country ISO', value: mobileMoneyInfo?.countryISO},
    {label: 'Phone Number', value: mobileMoneyInfo?.phoneNumber},
    {label: 'Mobile Money Network', value: mobileMoneyInfo?.mobileMoneyNetwork},
  ];

  // Sections are driven by the data itself rather than by the payment method,
  // so a newly added provider still renders without a code change here.
  const hasBankImage = images.length > 0;
  const showBankDetails = bankDetailFields.some(field => hasValue(field.value));
  const showMobileMoneyDetails = mobileMoneyFields.some(field =>
    hasValue(field.value),
  );

  const imageLabel = receiptImages.length
    ? receiptImages.length > 1
      ? `Receipts (${receiptImages.length})`
      : 'Receipt'
    : 'Bank Withdrawal Details';
  const detailsLabel =
    showBankDetails && !showMobileMoneyDetails
      ? 'Bank Details'
      : 'Payment Details';
  const toggleImageLabel = !viewReceipt
    ? `View ${imageLabel}`
    : showBankDetails || showMobileMoneyDetails
    ? `View ${detailsLabel}`
    : `Hide ${imageLabel}`;

  return (
    <div className={styles.root}>
      {hasValue(paymentMethod) && (
        <div className={styles.providerBadge}>
          Payment Method: {String(paymentMethod).replace(/_/g, ' ')}
        </div>
      )}

      {!showBankDetails && !showMobileMoneyDetails && !hasBankImage && (
        <div className={styles.emptyState}>
          No payment details available for this transaction.
        </div>
      )}

      {showMobileMoneyDetails && !viewReceipt && (
        <div className={styles.section}>
          <div className={styles.top}>
            <h1>Mobile Money Details</h1>
          </div>
          {renderDetailGrid(mobileMoneyFields)}
        </div>
      )}

      {showBankDetails && !viewReceipt && (
        <div className={styles.section}>
          <div className={styles.top}>
            <h1>User Bank Details</h1>
          </div>
          {renderDetailGrid(bankDetailFields)}
        </div>
      )}

      {hasBankImage && viewReceipt && (
        <div className={styles.section}>
          <div className={styles.top}>
            <h1>{imageLabel}</h1>
          </div>
          {loading ? (
            <div className={styles.receipt}>
              <Spin size="large" />
            </div>
          ) : (
            presignedUrls.map((url, index) =>
              url ? (
                <div className={styles.receipt} key={images[index]}>
                  {presignedUrls.length > 1 && (
                    <div className={styles.receiptCaption}>
                      <span>
                        {index + 1} of {presignedUrls.length}
                      </span>
                      <button
                        type="button"
                        className={styles.receiptDownload}
                        onClick={() =>
                          downloadUrl(url, `wire-transfer-receipt-${index + 1}`)
                        }>
                        Download
                      </button>
                    </div>
                  )}
                  <img src={url} alt={`Receipt ${index + 1}`} />
                </div>
              ) : null,
            )
          )}
        </div>
      )}

      {hasBankImage && (
        <div className={styles.modalButtons}>
          <button
            className={styles.button}
            disabled={loading}
            onClick={handleToggleReceipt}>
            {toggleImageLabel}
          </button>
          <button
            disabled={loading}
            className={`${styles.button} ${styles.secondaryButton}`}
            onClick={handleDownloadImage}>
            Download{' '}
            {receiptImages.length > 1
              ? 'Receipts'
              : receiptImages.length
              ? 'Receipt'
              : 'Instructions'}
          </button>
        </div>
      )}
    </div>
  );
};

export default WireTransferDetails;
